import { z } from 'zod';
import { createEndpoint, StandardSuccessResponseSchema } from '../createEndpoint';
import type { HttpClient } from '../httpClient';

const BackgroundSchema = z.object({
    id: z.union([z.number(), z.string()]).nullable(),
    key: z.string(),
    url: z.string().url(),
    cdn_url: z.string().url(),
    is_preset: z.boolean(),
    filename: z.string().optional(),
});

const BackgroundListSchema = z.object({
    success: z.literal(true),
    data: z.array(BackgroundSchema),
    limit: z.number(),
});

export function createExpertReviewShareBackgroundsApi(http: HttpClient, baseUrl: string) {
    const base = `${baseUrl}/expert/review-share-backgrounds`;
    const save = createEndpoint<Omit<z.infer<typeof BackgroundSchema>, 'is_preset'>, { media_id: string }>(http, 'POST', '/', {
        base,
        // Upload responses are media resources and do not include is_preset.
        responseSchema: BackgroundSchema.omit({ is_preset: true }),
    });

    return {
        generateShare: createEndpoint<{
            share_url: string; image_url: string; version: string; expires_at: string;
        }, { background_key: string | null }>(http, 'POST', '/:id/share', {
            base: `${baseUrl}/expert/reviews`,
            params: ['id'],
            responseSchema: z.object({ share_url: z.string().url(), image_url: z.string().url(), version: z.string(), expires_at: z.string() }),
        }),
        // Preserve the top-level limit, which createEndpoint's envelope unwrap discards.
        async list() {
            const response = await http.get(base);
            return BackgroundListSchema.parse(response.data);
        },
        async save(mediaId: string) {
            return { ...await save({ body: { media_id: mediaId } }), is_preset: false as const };
        },
        delete: createEndpoint(http, 'DELETE', '/:id', {
            base,
            params: ['id'],
            responseSchema: StandardSuccessResponseSchema,
        }),
    };
}
